import { useState, useEffect, useCallback } from 'react';
import { Wand2, RefreshCw, Save, Send, Plus, X, Pencil, CheckCircle } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

interface GeneratedJD {
  summary: string;
  responsibilities: string[];
  requirements: string[];
  nice_to_have: string[];
  benefits_blurb: string;
  seo_keywords: string[];
}

interface Job {
  id: string;
  title: string;
  status: string;
}

type JDField = 'summary' | 'responsibilities' | 'requirements' | 'nice_to_have' | 'benefits_blurb';

// ─── Tag Input ────────────────────────────────────────────────

function TagInput({ value, onChange, placeholder }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [input, setInput] = useState('');

  function add() {
    const trimmed = input.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInput('');
  }

  return (
    <div className="flex flex-wrap gap-1.5 p-2 bg-surface-3 border border-border rounded-lg min-h-[42px]">
      {value.map(tag => (
        <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs">
          {tag}
          <button onClick={() => onChange(value.filter(v => v !== tag))} className="hover:text-white">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
        placeholder={value.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[100px] bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
}

// ─── Refine Inline ────────────────────────────────────────────

function RefineInline({ field, currentJD, onRefined }: { field: JDField; currentJD: GeneratedJD; onRefined: (updated: GeneratedJD) => void }) {
  const [instruction, setInstruction] = useState('');
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  async function refine() {
    if (!instruction.trim()) return;
    setLoading(true);
    try {
      const res = await fetch('/api/people/jd-generator/refine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_jd: currentJD, field, instruction }),
      });
      if (!res.ok) throw new Error('Refine failed');
      const updated = await res.json() as GeneratedJD;
      onRefined(updated);
      setShow(false);
      setInstruction('');
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  if (!show) return (
    <button onClick={() => setShow(true)} className="ml-2 text-muted-foreground hover:text-amber-400" title="Refine with AI">
      <Pencil className="w-3.5 h-3.5" />
    </button>
  );

  return (
    <div className="mt-2 flex gap-2 items-center">
      <input
        value={instruction}
        onChange={e => setInstruction(e.target.value)}
        placeholder="e.g. Make it more concise and MSP-focused..."
        className="flex-1 bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-xs text-foreground"
        onKeyDown={e => e.key === 'Enter' && void refine()}
      />
      <button
        onClick={() => void refine()}
        disabled={loading || !instruction.trim()}
        className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs hover:bg-amber-600 disabled:opacity-50"
      >
        {loading ? '...' : 'Refine'}
      </button>
      <button onClick={() => setShow(false)} className="text-muted-foreground hover:text-foreground">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function JobDescriptionGeneratorPage() {
  const [form, setForm] = useState({
    role_title: '',
    level: 'mid' as 'junior' | 'mid' | 'senior' | 'lead' | 'principal',
    department: '',
    required_skills: [] as string[],
    nice_to_have_skills: [] as string[],
    employment_type: 'full_time' as 'full_time' | 'part_time' | 'contract' | 'intern',
    remote_policy: 'hybrid' as 'remote' | 'hybrid' | 'onsite',
  });

  const [jd, setJD] = useState<GeneratedJD | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedName, setSavedName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Load jobs for publish dropdown
  const loadJobs = useCallback(async () => {
    try {
      const res = await fetch('/api/jobs?status=draft&limit=50');
      if (res.ok) {
        const data = await res.json() as { jobs: Job[] };
        setJobs(data.jobs ?? []);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => { void loadJobs(); }, [loadJobs]);

  async function generate() {
    if (!form.role_title.trim() || !form.department.trim()) {
      setError('Role title and department are required');
      return;
    }
    setGenerating(true);
    setError(null);
    setPublishSuccess(false);
    try {
      const res = await fetch('/api/people/jd-generator/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Generation failed');
      const data = await res.json() as GeneratedJD;
      setJD(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setGenerating(false);
    }
  }

  async function publishToJob() {
    if (!jd || !selectedJobId) return;
    setPublishing(true);
    try {
      const res = await fetch(`/api/people/jd-generator/publish-to-job/${selectedJobId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(jd),
      });
      if (!res.ok) throw new Error('Publish failed');
      setPublishSuccess(true);
    } catch {
      // silent
    } finally {
      setPublishing(false);
    }
  }

  async function saveAsTemplate() {
    if (!jd || !savedName.trim()) return;
    setSaving(true);
    try {
      await fetch('/api/people/jd-generator/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: savedName,
          role_family: form.department,
          jd,
          role_title: form.role_title,
          level: form.level,
          department: form.department,
          employment_type: form.employment_type,
          remote_policy: form.remote_policy,
        }),
      });
      setShowSaveModal(false);
      setSavedName('');
    } catch { /* silent */ } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Wand2 className="w-5 h-5 text-amber-500" />
        <h1 className="text-xl font-bold text-foreground">AI Job Description Generator</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Form */}
        <div className="glass rounded-xl p-6 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">Job Details</h2>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs text-muted-foreground block mb-1">Role Title *</label>
              <input
                value={form.role_title}
                onChange={e => setForm(f => ({ ...f, role_title: e.target.value }))}
                placeholder="e.g. Senior Network Engineer"
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Level</label>
              <select
                value={form.level}
                onChange={e => setForm(f => ({ ...f, level: e.target.value as typeof form.level }))}
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              >
                {['junior', 'mid', 'senior', 'lead', 'principal'].map(l => (
                  <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Department *</label>
              <input
                value={form.department}
                onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                placeholder="e.g. Network Operations"
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Employment Type</label>
              <select
                value={form.employment_type}
                onChange={e => setForm(f => ({ ...f, employment_type: e.target.value as typeof form.employment_type }))}
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              >
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="intern">Intern</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Remote Policy</label>
              <select
                value={form.remote_policy}
                onChange={e => setForm(f => ({ ...f, remote_policy: e.target.value as typeof form.remote_policy }))}
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              >
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
                <option value="onsite">On-site</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="text-xs text-muted-foreground block mb-1">Required Skills (press Enter to add)</label>
              <TagInput
                value={form.required_skills}
                onChange={v => setForm(f => ({ ...f, required_skills: v }))}
                placeholder="e.g. Cisco IOS, Azure, PowerShell..."
              />
            </div>

            <div className="col-span-2">
              <label className="text-xs text-muted-foreground block mb-1">Nice-to-Have Skills</label>
              <TagInput
                value={form.nice_to_have_skills}
                onChange={v => setForm(f => ({ ...f, nice_to_have_skills: v }))}
                placeholder="e.g. Python, Terraform..."
              />
            </div>
          </div>

          <button
            onClick={() => void generate()}
            disabled={generating}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm font-medium disabled:opacity-50"
          >
            {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {generating ? 'Generating...' : 'Generate Job Description'}
          </button>
        </div>

        {/* Right: Preview */}
        <div className="glass rounded-xl p-6 space-y-4 min-h-[400px]">
          {!jd ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center py-16">
              <Wand2 className="w-12 h-12 text-amber-500/20" />
              <p className="text-sm text-muted-foreground">Fill in the details and click Generate to create an AI-powered MSP job description.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">{form.role_title} — {form.level}</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowSaveModal(true)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-surface-3 hover:bg-surface-2 text-xs text-foreground"
                  >
                    <Save className="w-3.5 h-3.5" /> Save Template
                  </button>
                </div>
              </div>

              <div className="space-y-4 text-sm">
                {/* Summary */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wide">Summary</h3>
                    <RefineInline field="summary" currentJD={jd} onRefined={setJD} />
                  </div>
                  <p className="text-foreground/90 leading-relaxed">{jd.summary}</p>
                </div>

                {/* Responsibilities */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wide">Responsibilities</h3>
                    <RefineInline field="responsibilities" currentJD={jd} onRefined={setJD} />
                  </div>
                  <ul className="space-y-1">
                    {jd.responsibilities.map((r, i) => (
                      <li key={i} className="flex gap-2 text-foreground/90">
                        <span className="text-amber-500 mt-0.5 shrink-0">•</span>
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Requirements */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wide">Requirements</h3>
                    <RefineInline field="requirements" currentJD={jd} onRefined={setJD} />
                  </div>
                  <ul className="space-y-1">
                    {jd.requirements.map((r, i) => (
                      <li key={i} className="flex gap-2 text-foreground/90">
                        <span className="text-amber-500 mt-0.5 shrink-0">•</span>
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Nice to Have */}
                {jd.nice_to_have.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wide">Nice to Have</h3>
                      <RefineInline field="nice_to_have" currentJD={jd} onRefined={setJD} />
                    </div>
                    <ul className="space-y-1">
                      {jd.nice_to_have.map((r, i) => (
                        <li key={i} className="flex gap-2 text-muted-foreground">
                          <span className="text-zinc-500 mt-0.5 shrink-0">◦</span>
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wide">Benefits</h3>
                    <RefineInline field="benefits_blurb" currentJD={jd} onRefined={setJD} />
                  </div>
                  <p className="text-foreground/90 leading-relaxed">{jd.benefits_blurb}</p>
                </div>

                {/* SEO Keywords */}
                {jd.seo_keywords.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">SEO Keywords</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {jd.seo_keywords.map((k, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 text-xs">{k}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Publish to Job */}
              <div className="border-t border-border pt-4 space-y-2">
                <h3 className="text-xs font-semibold text-muted-foreground">Publish to Job Posting</h3>
                {publishSuccess ? (
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Published successfully!
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <select
                      value={selectedJobId}
                      onChange={e => setSelectedJobId(e.target.value)}
                      className="flex-1 bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
                    >
                      <option value="">Select a job posting...</option>
                      {jobs.map(j => (
                        <option key={j.id} value={j.id}>{j.title}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => void publishToJob()}
                      disabled={!selectedJobId || publishing}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-sm hover:bg-amber-600 disabled:opacity-50"
                    >
                      <Send className="w-3.5 h-3.5" />
                      {publishing ? 'Publishing...' : 'Publish'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Save Template Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Save as Template</h3>
              <button onClick={() => setShowSaveModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Template Name</label>
              <input
                value={savedName}
                onChange={e => setSavedName(e.target.value)}
                placeholder={`${form.role_title} (${form.level})`}
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
                autoFocus
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowSaveModal(false)} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
              <button
                onClick={() => void saveAsTemplate()}
                disabled={!savedName.trim() || saving}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-amber-500 text-white text-sm hover:bg-amber-600 disabled:opacity-50"
              >
                <Plus className="w-3.5 h-3.5" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
